import { useMutation, useQueryClient } from '@tanstack/react-query';
import { optInFeaturesQueryKeys } from '../Constants/queryKeys';
import { deleteOptInUser } from '../Services/OptInFeatures.service';
import { useActionToast } from '@/Hooks/useActionToast';

/**
 * Erase one person's opt-in data on one feature — `DELETE /:id/users/:platformUserId` (R30).
 *
 * It is the purge at the scale of one person, so it invalidates the same four caches: that person
 * was counted in every headline number, in the daily series, in the reasons for leaving and in the
 * people table. A row leaving the table while "Já ligaram" keeps counting it is the same visible
 * contradiction, one person wide.
 *
 * Nothing is optimistic — the row goes when the refetch says it is gone, and a failure leaves it
 * where it was.
 */
export const useDeleteOptInUser = (featureId: string) => {
  const queryClient = useQueryClient();
  const toast = useActionToast();

  const invalidateAdoption = (): void => {
    void queryClient.invalidateQueries({ queryKey: optInFeaturesQueryKeys.adoption(featureId) });
    void queryClient.invalidateQueries({
      queryKey: [...optInFeaturesQueryKeys.all, 'users', featureId],
    });
    void queryClient.invalidateQueries({ queryKey: optInFeaturesQueryKeys.detail(featureId) });
    void queryClient.invalidateQueries({ queryKey: optInFeaturesQueryKeys.lists() });
  };

  return useMutation({
    mutationFn: (platformUserId: number) => deleteOptInUser(featureId, platformUserId),
    onSuccess: result => {
      invalidateAdoption();

      toast.success(
        `Dados da pessoa ${result.platformUserId} apagados`,
        'Ela volta ao comportamento padrão e passa a contar como alguém que nunca optou. A resposta de pesquisa dela continua guardada.',
      );
    },
    onError: (error: Error) => {
      // Refetch on failure: it is what takes away a row somebody else had already erased.
      invalidateAdoption();
      toast.failure('Não foi possível apagar os dados desta pessoa', error);
    },
  });
};
